import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, Award, AlertTriangle } from 'lucide-react';
import ScoreBadge from '../components/ScoreBadge';
import { BACKEND_URL } from '../lib/config';

const SCORE_COLORS = { 1: '#EF4444', 2: '#F97316', 3: '#EAB308', 4: '#22C55E', 5: '#10B981' };

// Module-level constants prevent new object references on every render (avoids unnecessary child re-renders)
const BAR_CHART_MARGIN = { left: 0, right: 20, top: 5, bottom: 5 };
const TOOLTIP_CONTENT_STYLE = { background: '#111827', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 };
const TOOLTIP_LABEL_STYLE = { color: '#EAF0F6', fontSize: 11 };
const TOOLTIP_LABEL_STYLE_PIE = { color: '#EAF0F6' };
const TOOLTIP_ITEM_STYLE = { color: '#2BB5D8' };
const XAXIS_TICK = { fill: '#8B95A1', fontSize: 10 };
const YAXIS_TICK = { fill: '#8B95A1', fontSize: 9 };

// ── Extracted chart components ────────────────────────────────────────────────

function SkillAveragesChart({ data: chartData }) {
  if (!chartData?.length) return null;
  return (
    <Card className="bg-[#111827] border-border/60">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Top Skill Averages</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData} layout="vertical" margin={BAR_CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis type="number" domain={[0, 5]} tick={XAXIS_TICK} />
            <YAxis type="category" dataKey="skill" tick={YAXIS_TICK} width={130} />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
            />
            <Bar dataKey="average" fill="#2BB5D8" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function ScoreDistributionChart({ data: chartData }) {
  if (!chartData?.length) return null;
  return (
    <Card className="bg-[#111827] border-border/60">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Score Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <ResponsiveContainer width="60%" height={220}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="count"
                nameKey="label"
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={40}
              >
                {chartData.map(entry => (
                  <Cell key={`score-cell-${entry.score}`} fill={SCORE_COLORS[entry.score]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE_PIE}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5">
            {chartData.map(entry => (
              <div key={entry.score} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: SCORE_COLORS[entry.score] }} />
                <span className="text-xs">{entry.label}: {entry.count}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PerformersList({ title, icon: Icon, iconColor, players, emptyText }) {
  return (
    <Card className="bg-[#111827] border-border/60">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon size={14} className={iconColor} /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {players?.length ? (
          <div className="space-y-1.5">
            {players.slice(0, 8).map((p, i) => (
              <div
                key={`${p.name}-${p.batch_name}-${i}`}
                className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-secondary/30"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                  <div>
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {p.batch_name}{p.cycle_name ? ` · ${p.cycle_name}` : ''}
                    </p>
                  </div>
                </div>
                <ScoreBadge score={p.average} showLabel />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">{emptyText}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filter, setFilter] = useState({ batch_id: '', location_id: '' });

  // BACKEND_URL, axios, all set* functions are stable module/useState refs.
  // Only `filter` is volatile — this is the correct deps array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, l] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics`, { params: filter }),
        axios.get(`${BACKEND_URL}/api/batches`),
        axios.get(`${BACKEND_URL}/api/locations`),
      ]);
      setData(a.data);
      setBatches(b.data);
      setLocations(l.data);
    } catch (e) { console.error('Analytics load error:', e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const STAT_CARDS = [
    { label: 'Total Evaluations', value: data?.total_evaluations, icon: TrendingUp, color: 'text-primary' },
    { label: 'Total Players', value: data?.total_players, icon: Award, color: 'text-[#F7941D]' },
    { label: 'Overall Average', value: data?.overall_average, icon: TrendingUp, color: 'text-green-400' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-4">
          {[1, 2, 3].map(i => (
            <div key={`skel-${i}`} className="h-48 bg-[#111827] rounded-xl animate-pulse" />
          ))}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
        {/* Filters */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Analytics</h1>
            <p className="text-sm text-muted-foreground">Performance insights across evaluations</p>
          </div>
          <div className="flex gap-2">
            <Select
              value={filter.location_id || 'all'}
              onValueChange={v => setFilter(p => ({ ...p, location_id: v === 'all' ? '' : v, batch_id: '' }))}
            >
              <SelectTrigger className="w-[140px] h-9 bg-secondary/50 border-border/60 text-xs">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-border/60">
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select
              value={filter.batch_id || 'all'}
              onValueChange={v => setFilter(p => ({ ...p, batch_id: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-[130px] h-9 bg-secondary/50 border-border/60 text-xs">
                <SelectValue placeholder="All Batches" />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-border/60">
                <SelectItem value="all">All Batches</SelectItem>
                {(filter.location_id
                  ? batches.filter(b => {
                      const sel = locations.find(l => l.id === filter.location_id);
                      return (sel?.batch_ids || []).includes(b.id);
                    })
                  : batches
                ).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!data || data.total_evaluations === 0 ? (
          <div className="text-center py-20">
            <TrendingUp size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="font-medium">No analytics data yet</p>
            <p className="text-sm text-muted-foreground mt-1">Upload evaluations to see insights here.</p>
          </div>
        ) : (
          <>
            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-4">
              {STAT_CARDS.map(({ label, value, icon: Icon, color }) => (
                <Card key={label} className="bg-[#111827] border-border/60 card-hover">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{label}</p>
                        <p className={`text-3xl font-bold ${color}`}>{value ?? '–'}</p>
                      </div>
                      <div className="w-10 h-10 bg-secondary/70 rounded-xl flex items-center justify-center">
                        <Icon size={18} className={color} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-4">
              <SkillAveragesChart data={data?.skill_averages} />
              <ScoreDistributionChart data={data?.score_distribution} />
            </div>

            {/* Category Averages */}
            {data?.category_averages?.length > 0 && (
              <Card className="bg-[#111827] border-border/60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Category Averages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {data.category_averages.map(cat => (
                      <div key={cat.category} className="text-center p-4 bg-secondary/40 rounded-xl">
                        <p className="text-2xl font-bold text-primary">{cat.average}</p>
                        <p className="text-xs text-muted-foreground mt-1">{cat.category}</p>
                        <div className="mt-1"><ScoreBadge score={cat.average} /></div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Performers */}
            <div className="grid lg:grid-cols-2 gap-4">
              <PerformersList
                title="Top Performers"
                icon={Award}
                iconColor="text-[#F7941D]"
                players={data?.top_performers}
                emptyText="No data yet"
              />
              <PerformersList
                title="Needs Improvement"
                icon={AlertTriangle}
                iconColor="text-destructive"
                players={data?.needs_improvement}
                emptyText="No data yet"
              />
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
